import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api-client';

/**
 * Check if the device is a mobile device (phone or tablet).
 */
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = ['android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone', 'mobile', 'tablet'];
  const isMobile = mobileKeywords.some(keyword => userAgent.includes(keyword));
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 1024;
  
  return isMobile || (hasTouch && isSmallScreen);
};

const PUSH_AUTO_SUBSCRIBE_KEY = 'push_auto_subscribe_attempted';

interface PushNotificationContextType {
  isSupported: boolean;
  isSubscribed: boolean; // Browser has subscription AND it belongs to current user
  permission: NotificationPermission;
  loading: boolean;
  
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  requestPermission: () => Promise<boolean>;
  
  // Preferences from communication_preference
  pushMarketingEnabled: boolean;
  pushAccountEnabled: boolean;
  updatePreferences: (marketing: boolean, account: boolean) => Promise<void>;
}

const PushNotificationContext = createContext<PushNotificationContextType | null>(null);

export const usePushNotificationContext = () => {
  const ctx = useContext(PushNotificationContext);
  if (!ctx) throw new Error('usePushNotificationContext must be used within PushNotificationProvider');
  return ctx;
};

export const PushNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isGeoComplyCompliant } = useAuth();
  // Pass userId to hook so it can verify/re-register subscription when user changes
  const pushHook = usePushNotifications(user?.id);
  
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [pushMarketingEnabled, setPushMarketingEnabled] = useState(true);
  const [pushAccountEnabled, setPushAccountEnabled] = useState(true);
  
  // Track if we've already attempted auto-subscribe for this user session
  const autoSubscribeAttempted = useRef<number | null>(null);

  // Fetch preferences on mount
  useEffect(() => {
    if (user) {
      apiClient.get<{ success: boolean; preferences: { push_marketing?: boolean; push_account?: boolean } }>('/profile/communication-preferences')
        .then(response => {
          if (response.success && response.preferences) {
            setPushMarketingEnabled(response.preferences.push_marketing ?? true);
            setPushAccountEnabled(response.preferences.push_account ?? true);
          }
        })
        .catch(() => {
          // Silently fail - preferences check is not critical
        });
    }
  }, [user]);

  // Auto-subscribe users on mobile devices (once per device)
  // This triggers the browser's native permission dialog automatically
  useEffect(() => {
    // Skip if still loading or no user
    if (pushHook.loading || !user) return;
    
    // Skip if we already attempted this session
    if (autoSubscribeAttempted.current === user.id) return;
    
    // Only auto-subscribe on mobile devices
    if (!isMobileDevice()) {
      console.debug('[Push] Auto-subscribe skipped - not a mobile device');
      return;
    }
    
    // Skip if push not supported
    if (!pushHook.isSupported) {
      console.debug('[Push] Auto-subscribe skipped - not supported');
      return;
    }
    
    // Skip if already subscribed for current user
    if (pushHook.isSubscribedForCurrentUser) {
      console.debug('[Push] Auto-subscribe skipped - already subscribed');
      autoSubscribeAttempted.current = user.id;
      return;
    }
    
    // Skip if permission was explicitly denied
    if (pushHook.permission === 'denied') {
      console.debug('[Push] Auto-subscribe skipped - permission denied');
      autoSubscribeAttempted.current = user.id;
      return;
    }
    
    // Check if we've already attempted on this device (persisted in localStorage)
    // Only re-prompt if permission was reset to 'default' (e.g., PWA reinstall)
    const previouslyAttempted = localStorage.getItem(PUSH_AUTO_SUBSCRIBE_KEY);
    if (previouslyAttempted && pushHook.permission !== 'default') {
      console.debug('[Push] Auto-subscribe skipped - already attempted on this device');
      autoSubscribeAttempted.current = user.id;
      return;
    }
    
    // Wait for GeoComply to complete first to avoid dialog conflicts
    if (isGeoComplyCompliant === null) {
      console.debug('[Push] Auto-subscribe waiting for GeoComply...');
      return;
    }
    
    // Mark as attempted (both in-memory and localStorage)
    autoSubscribeAttempted.current = user.id;
    localStorage.setItem(PUSH_AUTO_SUBSCRIBE_KEY, Date.now().toString());
    
    // Delay slightly to avoid conflicts with other permission dialogs
    const timer = setTimeout(() => {
      console.debug('[Push] Auto-subscribing user on mobile device');
      pushHook.subscribe().then(success => {
        console.debug('[Push] Auto-subscribe result:', success);
      }).catch(error => {
        console.error('[Push] Auto-subscribe error:', error);
      });
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [user, pushHook.loading, pushHook.isSupported, pushHook.isSubscribedForCurrentUser, pushHook.permission, pushHook.subscribe, isGeoComplyCompliant]);

  const updatePreferences = useCallback(async (marketing: boolean, account: boolean) => {
    if (!user) return;
    
    try {
      setPrefsLoading(true);
      await apiClient.put('/profile/communication-preferences', {
        push_marketing: marketing,
        push_account: account,
      });
      setPushMarketingEnabled(marketing);
      setPushAccountEnabled(account);
    } catch (error) {
      console.error('Failed to update push preferences:', error);
      throw error;
    } finally {
      setPrefsLoading(false);
    }
  }, [user]);

  // Combine hook loading with preferences loading
  const loading = pushHook.loading || prefsLoading;

  return (
    <PushNotificationContext.Provider
      value={{
        isSupported: pushHook.isSupported,
        // Use isSubscribedForCurrentUser so we prompt new users even if browser has subscription from different user
        isSubscribed: pushHook.isSubscribedForCurrentUser,
        permission: pushHook.permission,
        loading,
        subscribe: pushHook.subscribe,
        unsubscribe: pushHook.unsubscribe,
        requestPermission: pushHook.requestPermission,
        pushMarketingEnabled,
        pushAccountEnabled,
        updatePreferences,
      }}
    >
      {children}
    </PushNotificationContext.Provider>
  );
};
